import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ensureProfile } from '@/utils/ensureProfile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Globe } from 'lucide-react';

export default function Auth() {
  const { t, toggleLanguage, language } = useLanguage();
  const { error: authError, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');

  // Show loading if auth is still initializing
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Initializing...</p>
        </div>
      </div>
    );
  }

  // Show error only if it's a critical configuration error
  if (authError && authError.includes('configuration')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Configuration Error</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>
                <p className="font-semibold mb-2">Cannot connect to Supabase.</p>
                <p className="text-sm">Error: {authError}</p>
                <p className="text-sm mt-4">
                  Check if environment variables are set:
                </p>
                <ul className="text-sm mt-2 list-disc list-inside">
                  <li>VITE_SUPABASE_URL</li>
                  <li>VITE_SUPABASE_ANON_KEY</li>
                </ul>
                <a href="/debug" className="text-blue-600 hover:underline text-sm mt-4 block">
                  View debug info →
                </a>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('Attempting sign up with:', { email: loginMethod === 'email' ? email : 'N/A', phone: loginMethod === 'phone' ? phone : 'N/A' });
      const { data, error } = await supabase.auth.signUp({
        email: loginMethod === 'email' ? email : undefined,
        phone: loginMethod === 'phone' ? phone : undefined,
        password,
      });

      if (error) {
        console.error('Sign up error:', error);
        throw error;
      }

      console.log('Sign up successful:', data);

      if (data.user) {
        await ensureProfile(data.user.id);
      }
      toast.success('Account created! Check your email for verification.');
      setEmail('');
      setPhone('');
      setPassword('');
    } catch (error: any) {
      console.error('Sign up exception:', error);
      const errorMessage = error.message || 'Sign up failed';
      if (errorMessage.includes('already registered')) {
        toast.error('Email already registered. Try logging in instead.');
      } else if (errorMessage.includes('valid email')) {
        toast.error('Please enter a valid email address');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('Attempting sign in with:', { email: loginMethod === 'email' ? email : 'N/A', phone: loginMethod === 'phone' ? phone : 'N/A' });
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginMethod === 'email' ? email : undefined,
        phone: loginMethod === 'phone' ? phone : undefined,
        password,
      });

      if (error) {
        console.error('Sign in error:', error);
        console.error('Error code:', error.status);
        console.error('Full error object:', JSON.stringify(error));
        throw error;
      }

      console.log('Sign in successful:', data);

      if (data.user) {
        try {
          await ensureProfile(data.user.id, loginMethod === 'email' ? email : undefined);
          console.log('Profile ensured successfully');
        } catch (profileError) {
          console.error('Profile creation failed (non-critical):', profileError);
          // Don't fail login if profile creation fails
        }
      }
      
      // Wait a moment for profile to be ready
      await new Promise(resolve => setTimeout(resolve, 500));
      toast.success('Logged in successfully!');
    } catch (error: any) {
      console.error('Sign in exception:', error);
      const errorMessage = error.message || 'Login failed';
      
      // Provide helpful error messages
      if (errorMessage.includes('Invalid login credentials')) {
        toast.error('Invalid email or password');
      } else if (errorMessage.includes('not confirmed')) {
        toast.error('Email not verified. Check your email for verification link');
      } else if (errorMessage.includes('User not found')) {
        toast.error('User not found. Please sign up first.');
      } else {
        toast.error(errorMessage || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
      <div className="absolute top-4 right-4">
        <Button variant="outline" size="icon" onClick={toggleLanguage}>
          <Globe className="h-5 w-5" />
        </Button>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-3xl font-bold text-center">
            Broker Platform
          </CardTitle>
          <CardDescription className="text-center">
            {language === 'en' 
              ? 'Secure trading platform for professionals' 
              : 'منصة تداول آمنة للمحترفين'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">{t('login')}</TabsTrigger>
              <TabsTrigger value="register">{t('register')}</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-4">
              <form onSubmit={handleSignIn} className="space-y-4">
                <Tabs value={loginMethod} onValueChange={(v) => setLoginMethod(v as 'email' | 'phone')}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="email">{t('email')}</TabsTrigger>
                    <TabsTrigger value="phone">{t('phone')}</TabsTrigger>
                  </TabsList>

                  <TabsContent value="email" className="space-y-2 mt-4">
                    <Label htmlFor="login-email">{t('email')}</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </TabsContent>

                  <TabsContent value="phone" className="space-y-2 mt-4">
                    <Label htmlFor="login-phone">{t('phone')}</Label>
                    <Input
                      id="login-phone"
                      type="tel"
                      placeholder="+1234567890"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                  </TabsContent>
                </Tabs>

                <div className="space-y-2">
                  <Label htmlFor="login-password">{t('password')}</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? '...' : t('signIn')}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="space-y-4">
              <form onSubmit={handleSignUp} className="space-y-4">
                <Tabs value={loginMethod} onValueChange={(v) => setLoginMethod(v as 'email' | 'phone')}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="email">{t('email')}</TabsTrigger>
                    <TabsTrigger value="phone">{t('phone')}</TabsTrigger>
                  </TabsList>

                  <TabsContent value="email" className="space-y-2 mt-4">
                    <Label htmlFor="register-email">{t('email')}</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </TabsContent>

                  <TabsContent value="phone" className="space-y-2 mt-4">
                    <Label htmlFor="register-phone">{t('phone')}</Label>
                    <Input
                      id="register-phone"
                      type="tel"
                      placeholder="+1234567890"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                  </TabsContent>
                </Tabs>

                <div className="space-y-2">
                  <Label htmlFor="register-password">{t('password')}</Label>
                  <Input
                    id="register-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? '...' : t('signUp')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}